import { useMutation } from 'react-query';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { conciergeApi } from '../../services/concierge-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import { groupToPayload } from '../../lib/groupPayload';
import type { GroupDetail as GroupDetailType } from '../../types/groups';

function buildGroupContext(group: GroupDetailType) {
  return {
    id: group.id,
    slug: group.slug,
    title: group.title,
    subtitle: group.subtitle,
    location_name: group.locationName,
    date_start: group.dateStart,
    date_end: group.dateEnd,
    story: group.story,
    external_rsvp_url: group.externalRsvpUrl,
    room_blocks: group.roomBlocks.map((block) => ({
      hotel_name: block.hotelName,
      room_type: block.roomType,
      nightly_rate: block.nightlyRate,
      currency: block.currency,
      total_rooms: block.totalRooms,
      available_rooms: block.availableRooms,
      booking_deadline: block.bookingDeadline,
      description: block.description,
      amenities: block.amenities || [],
      notes: block.notes,
    })),
    itinerary_items: group.itineraryItems.map((item) => ({
      starts_at: item.startsAt,
      title: item.title,
      description: item.description,
      location_name: item.locationName,
      cta_label: item.ctaLabel,
      cta_url: item.ctaUrl,
    })),
  };
}

function buildConciergeConfig(group: GroupDetailType) {
  return {
    persona_name: 'Tripsby Concierge',
    greeting: `Hi! I'm the Tripsby concierge for ${group.title}. I can help with the itinerary, room block, travel logistics, and questions about the event.`,
    tone: 'warm_friendly',
    formality: 'casual',
    language: 'en',
    restrictions: [
      'Do not invent event details, room availability, prices, or host instructions that are not in the Tripsby Studio group context.',
      'If a guest asks to book a room, explain that Tripsby will follow up after they submit the room-interest form.',
    ],
    fallback_message: "I don't have that detail yet — please check with the host or Tripsby ops.",
    travel_features_enabled: true,
    group_context: buildGroupContext(group),
  };
}

export default function ConciergeTab({ group, onChanged }: { group: GroupDetailType; onChanged: () => void }) {
  const conciergeMutation = useMutation(async ({ provisionNumber }: { provisionNumber: boolean }) => {
    const config = buildConciergeConfig(group);
    let instanceId = group.conciergeInstanceCode || '';

    if (!instanceId) {
      const created = await conciergeApi.createConcierge({
        name: `${group.title} Concierge`,
        description: `Tripsby group concierge for ${group.title}`,
        use_case: 'event',
        event_date: group.dateStart || undefined,
        config,
      });
      instanceId = created.instance_id;
    } else {
      await conciergeApi.updateConfig(instanceId, config);
    }

    let provisioned: { phone_number?: string; status?: string } | null = null;
    if (provisionNumber) {
      provisioned = await conciergeApi.provisionConcierge(instanceId);
    }

    await groupsApi.updateGroup(group.id, groupToPayload(group, { conciergeInstanceCode: instanceId }));
    return { instanceId, provisioned };
  }, { onSuccess: onChanged });

  const saving = conciergeMutation.isLoading;
  const error = mutationErrorMessage(conciergeMutation.error);
  const hasDedicated = Boolean(group.conciergeInstanceCode);

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>Concierge routing</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {hasDedicated
                ? 'This group has its own attached Concierge instance. Keep the event context synced when itinerary or room blocks change.'
                : 'This group uses the shared Tripsby concierge by default. Guests can still open invite links and ask Tripsby about upcoming events; provision a dedicated number when the event needs its own SMS thread.'}
            </Typography>
          </Box>
          <Chip
            label={hasDedicated ? 'Dedicated concierge attached' : 'Shared pooled concierge'}
            color={hasDedicated ? 'success' : 'default'}
          />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography fontWeight={800}>Studio workflow</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Studio generates a concierge context packet from the group title, dates, itinerary, room blocks, RSVP link, and host notes.
          A dedicated concierge can be activated from here without leaving the group workflow.
        </Typography>
        {hasDedicated ? (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Concierge instance: <strong>{group.conciergeInstanceCode}</strong>
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" disabled={saving} onClick={() => conciergeMutation.mutate({ provisionNumber: false })}>
                {saving ? 'Syncing...' : 'Sync event context'}
              </Button>
              <Button variant="outlined" disabled={saving} onClick={() => conciergeMutation.mutate({ provisionNumber: true })}>
                {saving ? 'Activating...' : 'Activate/provision dedicated number'}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Alert severity="info">
              MVP default: use the shared Tripsby concierge for pooled support. Dedicated SMS is optional per event.
            </Alert>
            <Button variant="contained" disabled={saving} onClick={() => conciergeMutation.mutate({ provisionNumber: true })}>
              {saving ? 'Activating...' : 'Create concierge + provision dedicated number'}
            </Button>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
